import { useMemo } from 'react';
import { ITypeRequest } from '../../../theme/ui/Dashboard/Dashboard.model';
import { useAppSelector } from '../../store.model';
import { getMyWorkspaceSelector } from '../../workspace/selectors/Workspace.selectors';
import ReportAPI from '../ReportAPI';
import { skipToken } from '@reduxjs/toolkit/query';
import { patchLoadingState } from '../../../service/device/CommanderAPI/hooks/useAirConnectStatusCount';
import { ReportSumParamsAPI, ReportSumResponseAPI } from '../Report.model';
import { getI18n } from 'react-i18next';
import useMyWorkspacesChildren from '../../workspace/hooks/useMyWorkspacesChildren';
import _ from 'lodash';
import orComparator from '../../../shared/util/lodash/merge/orComparator';
import moment from 'moment';

const REPORT_TYPE_CONSUMPTION = 'PowerConsumption';
interface IUseConsumptionDataProps {
  groupId?: string;
  workspaceId?: string;
  typeRequest: ITypeRequest;
  timeFrame: string | undefined;
}

/** Format a sum response into { value, errorNoSchedule } */
function useFormattedSum(
  sumResponse: {
    data?: ReportSumResponseAPI;
    isLoading: boolean;
    isFetching: boolean;
    error?: unknown;
    currentData?: unknown;
    isUninitialized: boolean;
  },
  hasSchedule: boolean
) {
  const patched = patchLoadingState(sumResponse);

  const value = useMemo(() => {
    if (patched.data?.powerUsage_kJ == null) return undefined;
    // transform kJ to kWh
    const kwh = patched.data.powerUsage_kJ / 3600;
    return `${new Intl.NumberFormat(getI18n().language, {
      maximumFractionDigits: 0,
    }).format(kwh)} kWh`;
  }, [patched.data]);

  const errorNoSchedule = !hasSchedule && !patched.isLoading && !patched.error;
  return {
    ...patched,
    value,
    errorNoSchedule,
  };
}

/**
 * Create a function to get the time frame in milliseconds based on the selected time frame option.
 * Today it would not count because the report is not ready yet.
 *
 * day: yesterday 00:00 to yesterday 23:59
 * week: from yesterday the previous week, starting from the first day of that week, to yesterday the previous week,
 * ending on the last day of that week, ex: if today is Wednesday 10th in Germany, it would be from Monday 1st 00:00 to Sunday 7th 23:59
 * month: from yesterday the previous month, starting from the 1st day of that month, to yesterday the previous month,
 * ending on the last day of that month, ex: if today is June 10th, it would be from May 1st 00:00 to May 31st 23:59
 * year: from yesterday the previous year, starting from the 1st day of that year, to yesterday the previous year,
 * ending on the last day of that year, ex: if today is June 10th, 2024, it would be from January 1st, 2023 00:00 to December 31st, 2023 23:59
 *
 * @param timeFrame
 */
function getTimeFrameInMilliseconds(
  timeFrame: string | undefined
): ReportSumParamsAPI {
  const yesterday = moment().subtract(1, 'day');

  switch (timeFrame) {
    case 'day': {
      return {
        from: yesterday.startOf('day').valueOf(),
        to: yesterday.endOf('day').valueOf(),
      };
    }
    case 'week': {
      const previousWeek = yesterday.clone().subtract(1, 'week');
      return {
        from: previousWeek.startOf('isoWeek').valueOf(),
        to: previousWeek.endOf('isoWeek').valueOf(),
      };
    }
    case 'month': {
      const previousMonth = yesterday.clone().subtract(1, 'month');
      return {
        from: previousMonth.startOf('month').valueOf(),
        to: previousMonth.endOf('month').valueOf(),
      };
    }
    case 'year': {
      const previousYear = yesterday.clone().subtract(1, 'year');
      return {
        from: previousYear.startOf('year').valueOf(),
        to: previousYear.endOf('year').valueOf(),
      };
    }
    default: {
      // Default to previous week
      const defaultWeek = yesterday.clone().subtract(1, 'week');
      return {
        from: defaultWeek.startOf('isoWeek').valueOf(),
        to: defaultWeek.endOf('isoWeek').valueOf(),
      };
    }
  }
}

/**
 * Hook to fetch consumption data for a report.
 * To fetch the consumption data, we must get the list
 * Then select the report for consumption, then fetch the consumption data for that report.
 *
 * From the data include the groups that are composable devices, those groups should be inside of the entity selected
 */
function useConsumptionData(props: IUseConsumptionDataProps) {
  const { groupId, typeRequest, timeFrame } = props;
  const myWorkspaceId = useAppSelector(getMyWorkspaceSelector)?.id;
  const workspaceId = props.workspaceId ?? myWorkspaceId;

  const timeFrameParams = useMemo(
    () => (timeFrame ? getTimeFrameInMilliseconds(timeFrame) : undefined),
    [timeFrame]
  );

  // Get Report Types to find the report type for consumption
  const listReportTypesResponse = ReportAPI.endpoints.getReportTypes.useQuery(
    workspaceId ?? skipToken,
    { skip: !workspaceId || typeRequest === 'workspaces' || !timeFrameParams } // report types are only needed for group/workspace consumption, for user consumption we can directly fetch the sum without needing the report type
  );
  const { data: reportTypesData } = listReportTypesResponse;
  const reportTypeConsumption = useMemo(
    () =>
      reportTypesData?.find(
        (reportType) => reportType.name === REPORT_TYPE_CONSUMPTION
      ),
    [reportTypesData]
  );
  const reportTypeConsumptionId = reportTypeConsumption?.id;

  // Get Report Schedules to find the report schedule for consumption report type
  const listReportsScheduleResponse =
    ReportAPI.endpoints.getReportSchedules.useQuery(
      { workspaceId: workspaceId ?? '' },
      { skip: !workspaceId || typeRequest === 'workspaces' || !timeFrameParams } // report schedules are only needed for group consumption, for user consumption we can directly fetch the sum without needing the schedule
    );
  const { data: listReportsScheduleData } = listReportsScheduleResponse;
  const reportSchedulesForConsumptionType = useMemo(
    () =>
      listReportsScheduleData?.find(
        (reportSchedule) =>
          reportSchedule.reportSpec.reportType?.id === reportTypeConsumptionId
      ),
    [listReportsScheduleData, reportTypeConsumptionId]
  );
  const reportScheduleForConsumptionTypeId =
    reportSchedulesForConsumptionType?.id;

  const canFetchSum =
    !!reportScheduleForConsumptionTypeId && !!workspaceId && !!timeFrameParams;

  // Use server-side sum endpoints instead of fetching all data and filtering client-side
  const reportScheduleSumByUserResponse =
    ReportAPI.endpoints.getReportScheduleSumByUser.useQuery(
      {
        scheduleId: reportScheduleForConsumptionTypeId ?? '',
        workspaceId: workspaceId ?? '',
        params: timeFrameParams,
      },
      {
        skip: !canFetchSum || typeRequest !== 'user',
      }
    );

  const reportScheduleSumByGroupResponse =
    ReportAPI.endpoints.getReportScheduleSumByGroup.useQuery(
      {
        scheduleId: reportScheduleForConsumptionTypeId ?? '',
        workspaceId: workspaceId ?? '',
        groupId: groupId ?? '',
        params: timeFrameParams,
      },
      { skip: !canFetchSum || typeRequest !== 'group' }
    );

  const reportScheduleSumByWorkspaceResponse =
    ReportAPI.endpoints.getReportScheduleSum.useQuery(
      {
        scheduleId: reportScheduleForConsumptionTypeId ?? '',
        workspaceId: workspaceId ?? '',
        params: timeFrameParams,
      },
      { skip: !canFetchSum || typeRequest !== 'workspace' }
    );

  // Get all user workspaces for the 'workspaces' aggregation case
  const myWorkspacesResponse = useMyWorkspacesChildren({
    skip: typeRequest !== 'workspaces',
  });
  const workspaceIds = useMemo(
    () =>
      (myWorkspacesResponse.data && Object.keys(myWorkspacesResponse.data)) ??
      [],
    [myWorkspacesResponse.data]
  );

  const reportScheduleSumManyWorkspacesResponse =
    ReportAPI.endpoints.getReportManyWorkspacesScheduleSum.useQuery(
      { workspaceIds, params: timeFrameParams },
      {
        skip:
          typeRequest !== 'workspaces' ||
          workspaceIds.length === 0 ||
          !timeFrameParams,
      }
    );

  // Select the active response based on typeRequest
  const sumResponse = (() => {
    switch (typeRequest) {
      case 'user':
        return reportScheduleSumByUserResponse;
      case 'group':
        return reportScheduleSumByGroupResponse;
      case 'workspace':
        return reportScheduleSumByWorkspaceResponse;
      case 'workspaces':
        return reportScheduleSumManyWorkspacesResponse;
      default:
        return reportScheduleSumByUserResponse;
    }
  })();

  // For 'workspaces', schedule resolution is done inside queryFn, so always consider it "found"
  const hasSchedule =
    typeRequest === 'workspaces' ||
    (!!reportScheduleForConsumptionTypeId &&
      !listReportsScheduleResponse.isLoading &&
      !listReportsScheduleResponse.error &&
      !listReportsScheduleResponse.isUninitialized &&
      !listReportTypesResponse.isLoading &&
      !listReportTypesResponse.error &&
      !listReportTypesResponse.isUninitialized);

  const responseMerged = useMemo(() => {
    if (typeRequest === 'workspaces') return sumResponse;
    return {
      ..._.mergeWith(
        {},
        sumResponse,
        listReportsScheduleResponse,
        listReportTypesResponse,
        orComparator
      ),
      data: sumResponse.data,
      currentData: sumResponse.currentData,
    };
  }, [
    listReportTypesResponse,
    listReportsScheduleResponse,
    sumResponse,
    typeRequest,
  ]);
  return useFormattedSum(responseMerged, hasSchedule);
}

export default useConsumptionData;
